

const parseProductVariants = (form) => {
  const variantsScript = form.querySelector("[data-product-variants]");
  if (!variantsScript) return [];

  try {
    return JSON.parse(variantsScript.textContent || "[]");
  } catch (_) {
    return [];
  }
};

const createMoneyFormatter = (form) => {
  const moneyConfig = form.querySelector("[data-money-config]");
  const moneyFormat = moneyConfig?.dataset.moneyFormat || "";
  const currencyCode =
    moneyConfig?.dataset.currencyCode ||
    window.Shopify?.currency?.active ||
    "USD";

  if (window.Shopify?.formatMoney && moneyFormat) {
    return (cents) => window.Shopify.formatMoney(cents, moneyFormat);
  }

  return (cents) => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode,
    }).format(value);
  };
};

const variantMatchesSelection = (variant, selectedValues) => {
  if (selectedValues[0] && variant.option1 !== selectedValues[0]) return false;
  if (selectedValues[1] && variant.option2 !== selectedValues[1]) return false;
  if (selectedValues[2] && variant.option3 !== selectedValues[2]) return false;
  return true;
};

const getSelectedOptionValues = (optionsRoot) => {
  const selectedValues = [null, null, null];
  optionsRoot
    .querySelectorAll("[data-option-index].is-active")
    .forEach((el) => {
      const index = Number(el.dataset.optionIndex || 0);
      if (index >= 1 && index <= 3)
        selectedValues[index - 1] = el.dataset.optionValue || null;
    });
  return selectedValues;
};

const escapeSelectorValue = (value = "") => {
  if (window.CSS && typeof window.CSS.escape === "function") {
    return window.CSS.escape(String(value));
  }
  return String(value).replace(/"/g, '\\"');
};

const syncActiveOptionsFromVariant = (optionsRoot, variant) => {
  const optionValues = [variant.option1, variant.option2, variant.option3];
  optionValues.forEach((value, idx) => {
    if (!value) return;

    const index = idx + 1;
    optionsRoot
      .querySelectorAll(`[data-option-index="${index}"]`)
      .forEach((button) => button.classList.remove("is-active"));

    let selectedButton = optionsRoot.querySelector(
      `[data-option-index="${index}"][data-option-value="${escapeSelectorValue(value)}"]`,
    );
    if (!selectedButton && value) {
      const valLower = String(value).trim().toLowerCase();
      selectedButton = Array.from(
        optionsRoot.querySelectorAll(`[data-option-index="${index}"]`),
      ).find(
        (b) => String(b.dataset.optionValue || "").trim().toLowerCase() === valLower,
      );
    }
    selectedButton?.classList.add("is-active");

    const selectedOption = optionsRoot.querySelector(
      `[data-option-select][data-option-index="${index}"]`,
    );
    if (selectedOption) selectedOption.value = String(value);
  });
};

const updateOptionAvailability = (optionsRoot, variants) => {
  const selectedValues = getSelectedOptionValues(optionsRoot);
  const form = optionsRoot.closest("form.gyc-product-form");
  const sectionShell =
    form?.closest(".gyc-quick-view-modal") ||
    form?.closest(".section-shell") ||
    document;
  const isSticker =
    form?.dataset?.isSticker === "true" ||
    sectionShell?.dataset?.isSticker === "true" ||
    sectionShell?.querySelector?.('[data-is-sticker="true"]') !== null;
  const isSilicone =
    form?.dataset?.isSilicone === "true" ||
    sectionShell?.dataset?.isSilicone === "true" ||
    sectionShell?.classList?.contains?.("gyc-product-layout--silicone") ||
    sectionShell?.querySelector?.('[data-is-silicone="true"]') !== null;
  const hasAnyAvailable = variants.some((v) => Boolean(v.available));

  optionsRoot
    .querySelectorAll("[data-option-index][data-option-value]")
    .forEach((button) => {
      const index = Number(button.dataset.optionIndex || 0);
      const value = button.dataset.optionValue || "";
      if (!index || !value) return;

      const testSelection = [...selectedValues];
      testSelection[index - 1] = value;

      const hasAvailableVariant = variants.some(
        (variant) =>
          variant.available && variantMatchesSelection(variant, testSelection),
      );
      button.disabled = !hasAvailableVariant;
      button.setAttribute("aria-disabled", String(!hasAvailableVariant));
      button.classList.toggle("is-unavailable", !hasAvailableVariant);

      if ((isSilicone || isSticker) && hasAnyAvailable && !hasAvailableVariant) {
        button.style.display = "none";
      }
    });
};

const variantMediaIds = (variant) =>
  [
    variant?.featured_media?.id,
    variant?.featured_image?.id,
    variant?.color_image?.id,
  ]
    .filter((id) => id !== undefined && id !== null && id !== "")
    .map((id) => String(id));

const normalizeMediaUrl = (value = "") =>
  String(value)
    .split("?")[0]
    .replace(/^https?:/, "");

const isThumbNavigable = (thumb) => {
  if (!thumb || thumb.hidden) return false;
  if (
    thumb.disabled ||
    thumb.classList.contains("is-unavailable") ||
    thumb.getAttribute("aria-disabled") === "true"
  ) {
    return false;
  }
  if (thumb.style.display === "none") return false;
  return (
    thumb.offsetParent !== null ||
    window.getComputedStyle(thumb).display !== "none"
  );
};

const getGalleryThumbs = (sectionRoot) => {
  const form = sectionRoot.querySelector("form.gyc-product-form");
  const variants = form ? parseProductVariants(form) : [];
  const hasAnyAvailableVariant =
    variants.length === 0 || variants.some((v) => Boolean(v.available));

  const allThumbs = Array.from(
    sectionRoot.querySelectorAll("[data-gallery-thumb]"),
  ).filter(
    (thumb) =>
      thumb &&
      !thumb.hidden &&
      thumb.style.display !== "none" &&
      (thumb.offsetParent !== null ||
        window.getComputedStyle(thumb).display !== "none"),
  );

  if (!hasAnyAvailableVariant) {
    return allThumbs.length > 0 ? [allThumbs[0]] : [];
  }

  const navigableThumbs = allThumbs.filter(isThumbNavigable);
  return navigableThumbs.length > 0 ? navigableThumbs : allThumbs;
};

const updateGalleryNavVisibility = (sectionRoot) => {
  const form = sectionRoot.querySelector("form.gyc-product-form");
  const variants = form ? parseProductVariants(form) : [];
  const hasAnyAvailableVariant =
    variants.length === 0 || variants.some((v) => Boolean(v.available));
  const thumbs = getGalleryThumbs(sectionRoot);

  sectionRoot.querySelectorAll("[data-gallery-nav]").forEach((navBtn) => {
    if (!hasAnyAvailableVariant || thumbs.length <= 1) {
      navBtn.style.display = "none";
    } else {
      navBtn.style.removeProperty("display");
    }
  });
};

const updateGalleryThumbAvailability = (sectionRoot, variants) => {
  const soldOutText =
    sectionRoot.querySelector("[data-money-config]")?.dataset.soldOutText ||
    sectionRoot.querySelector("form.gyc-product-form [data-money-config]")
      ?.dataset.soldOutText ||
    "Sold out";
  const isSilicone =
    sectionRoot.querySelector('[data-is-silicone="true"]') !== null ||
    sectionRoot.classList.contains("gyc-product-layout--silicone") ||
    sectionRoot.dataset?.isSilicone === "true";
  const isSticker =
    sectionRoot.querySelector('[data-is-sticker="true"]') !== null ||
    sectionRoot.dataset?.isSticker === "true";

  const hasAnyAvailableVariant = variants.some((v) => Boolean(v.available));

  sectionRoot.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
    const mediaId = String(thumb.dataset.mediaId || "");
    const thumbSrc = normalizeMediaUrl(thumb.dataset.imageSrc || "");
    if (!mediaId && !thumbSrc) return;

    let isUnavailable = false;
    if (!hasAnyAvailableVariant) {
      isUnavailable = true;
    } else {
      const linkedVariants = variants.filter((variant) => {
        if (mediaId && variantMediaIds(variant).includes(mediaId)) return true;
        if (thumbSrc) {
          const vUrls = [
            variant?.featured_media?.preview_image?.src,
            variant?.featured_image?.src,
            variant?.color_image?.src,
          ]
            .filter(Boolean)
            .map(normalizeMediaUrl);
          if (
            vUrls.some(
              (u) =>
                u &&
                (u === thumbSrc ||
                  thumbSrc.endsWith(u.split("/").pop()) ||
                  u.endsWith(thumbSrc.split("/").pop())),
            )
          ) {
            return true;
          }
        }
        return false;
      });

      isUnavailable =
        linkedVariants.length > 0 &&
        linkedVariants.every((variant) => !variant.available);
    }

    thumb.classList.toggle("is-unavailable", isUnavailable);
    thumb.setAttribute("aria-disabled", String(isUnavailable));
    thumb.disabled = isUnavailable;
    if (isUnavailable) {
      thumb.setAttribute("tabindex", "-1");
    } else {
      thumb.removeAttribute("tabindex");
    }

    if ((isSilicone || isSticker) && hasAnyAvailableVariant && isUnavailable) {
      thumb.style.display = "none";
    }
    if (isUnavailable) {
      thumb.dataset.soldOutText = soldOutText;
    } else {
      delete thumb.dataset.soldOutText;
    }
  });

  const galleryMain = sectionRoot.querySelector(".gyc-product-gallery__main");
  if (galleryMain) {
    galleryMain.classList.toggle("is-out-of-stock", !hasAnyAvailableVariant);
  }
  const galleryStage = sectionRoot.querySelector(".gyc-product-gallery__stage");
  if (galleryStage) {
    galleryStage.classList.toggle("is-out-of-stock", !hasAnyAvailableVariant);
  }

  if (!hasAnyAvailableVariant) {
    const allThumbs = Array.from(
      sectionRoot.querySelectorAll("[data-gallery-thumb]"),
    );
    if (allThumbs.length > 0) {
      setActiveGalleryThumb(sectionRoot, allThumbs[0]);
      setMainGalleryImageFromThumb(sectionRoot, allThumbs[0]);
    }
  } else {
    const activeThumb = sectionRoot.querySelector(
      "[data-gallery-thumb].is-active",
    );
    if (
      activeThumb &&
      (activeThumb.disabled ||
        activeThumb.classList.contains("is-unavailable") ||
        activeThumb.getAttribute("aria-disabled") === "true")
    ) {
      const navigableThumbs = getGalleryThumbs(sectionRoot);
      if (navigableThumbs.length > 0) {
        setActiveGalleryThumb(sectionRoot, navigableThumbs[0]);
        setMainGalleryImageFromThumb(sectionRoot, navigableThumbs[0]);
      }
    }
  }

  updateGalleryNavVisibility(sectionRoot);
};
const getGalleryMain = (sectionRoot) =>
  sectionRoot.querySelector(".gyc-product-gallery__main");

const cssLengthToPixels = (value = "") => {
  const trimmedValue = String(value).trim();
  if (!trimmedValue) return 0;

  const parsedValue = Number.parseFloat(trimmedValue);
  if (!Number.isFinite(parsedValue)) return 0;

  if (trimmedValue.endsWith("rem")) {
    return (
      parsedValue *
      Number.parseFloat(
        getComputedStyle(document.documentElement).fontSize || "16",
      )
    );
  }

  return parsedValue;
};

const getScrollOffset = () => {
  const rootStyles = getComputedStyle(document.documentElement);
  const headerOffset = cssLengthToPixels(
    rootStyles.getPropertyValue("--gyc-header-offset"),
  );
  const stickyGap = cssLengthToPixels(
    rootStyles.getPropertyValue("--gyc-sticky-gap"),
  );
  return headerOffset + stickyGap + 8;
};

const scrollProductGalleryIntoView = (sectionRoot) => {
  if (sectionRoot?.classList?.contains("gyc-quick-view-modal") || sectionRoot?.closest?.(".gyc-quick-view-modal")) return;
  const gallery = sectionRoot.querySelector(".gyc-product-gallery");
  if (!gallery) return;

  const top =
    gallery.getBoundingClientRect().top + window.scrollY - getScrollOffset();
  window.scrollTo({
    top: Math.max(0, top),
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth",
  });
};

const preloadedGalleryCache = new Map();

const preloadGalleryImage = (src, srcset = "", sizes = "") => {
  if (!src) return null;
  const key = `${src}|${srcset}`;
  if (preloadedGalleryCache.has(key)) {
    return preloadedGalleryCache.get(key);
  }

  const img = new Image();
  if (sizes) img.sizes = sizes;
  if (srcset) img.srcset = srcset;
  img.src = src;

  preloadedGalleryCache.set(key, img);
  return img;
};

const preloadAllGalleryImages = (sectionRoot) => {
  const thumbs = getGalleryThumbs(sectionRoot);
  thumbs.forEach((thumb) => {
    const thumbImg = thumb.querySelector("img");
    const src =
      thumb.dataset.imageSrc || thumbImg?.currentSrc || thumbImg?.src || "";
    const srcset = thumb.dataset.imageSrcset || "";
    const sizes = thumb.dataset.imageSizes || "";
    if (src) {
      preloadGalleryImage(src, srcset, sizes);
    }
  });
};

let galleryLoadingTimeout = null;

const setGalleryMainLoading = (sectionRoot, isLoading, delayMs = 0) => {
  const galleryMain = getGalleryMain(sectionRoot);
  if (!galleryMain) return;

  if (galleryLoadingTimeout) {
    clearTimeout(galleryLoadingTimeout);
    galleryLoadingTimeout = null;
  }

  if (isLoading) {
    if (delayMs > 0) {
      galleryLoadingTimeout = setTimeout(() => {
        galleryMain.classList.add("is-loading");
      }, delayMs);
    } else {
      galleryMain.classList.add("is-loading");
    }
  } else {
    galleryMain.classList.remove("is-loading");
  }
};

const setMainGalleryImageSource = (
  sectionRoot,
  mainImage,
  imageSrc,
  imageAlt = "",
  mediaId = "",
  imageSrcset = "",
  imageSizes = "",
) => {
  if (!mainImage || !imageSrc) return false;

  const currentSrc = normalizeMediaUrl(
    mainImage.currentSrc || mainImage.src || "",
  );
  const nextSrc = normalizeMediaUrl(imageSrc);

  if (currentSrc === nextSrc) {
    mainImage.alt = imageAlt || mainImage.alt || "";
    mainImage.dataset.productMainImageId = mediaId || "";
    return true;
  }

  const updateImageDOM = () => {
    mainImage.src = imageSrc;
    if (imageSrcset) {
      mainImage.srcset = imageSrcset;
    } else {
      mainImage.removeAttribute("srcset");
    }
    if (imageSizes) {
      mainImage.sizes = imageSizes;
    } else {
      mainImage.removeAttribute("sizes");
    }
    mainImage.alt = imageAlt || mainImage.alt || "";
    mainImage.dataset.productMainImageId = mediaId || "";
    setGalleryMainLoading(sectionRoot, false);
  };

  const cachedImg = preloadGalleryImage(imageSrc, imageSrcset, imageSizes);
  if (cachedImg && typeof cachedImg.decode === "function") {
    cachedImg
      .decode()
      .then(updateImageDOM)
      .catch(updateImageDOM);
  } else {
    updateImageDOM();
  }

  return true;
};

const setActiveGalleryThumb = (sectionRoot, targetThumb) => {
  const thumbs = getGalleryThumbs(sectionRoot);
  if (!thumbs.length || !targetThumb) return null;

  thumbs.forEach((thumb) => {
    thumb.classList.remove("is-active");
    thumb.setAttribute("aria-current", "false");
  });

  targetThumb.classList.add("is-active");
  targetThumb.setAttribute("aria-current", "true");
  return targetThumb;
};

const setMainGalleryImageFromThumb = (sectionRoot, thumb) => {
  const mainImage = sectionRoot.querySelector(
    "[data-product-main-image], .gyc-product-gallery__image",
  );
  if (!mainImage || !thumb) return false;

  const thumbImage = thumb.querySelector("img");
  const imageSrc =
    thumb.dataset.imageSrc || thumbImage?.currentSrc || thumbImage?.src || "";
  if (!imageSrc) return false;
  const imageSrcset = thumb.dataset.imageSrcset || imageSrc;
  const imageSizes = thumb.dataset.imageSizes || mainImage.sizes || "";

  return setMainGalleryImageSource(
    sectionRoot,
    mainImage,
    imageSrc,
    thumb.dataset.imageAlt || mainImage.alt || "",
    thumb.dataset.mediaId || "",
    imageSrcset,
    imageSizes,
  );
};

const scrollThumbIntoView = (sectionRoot, thumb) => {
  if (!thumb) return;
  const isQuickView = sectionRoot.classList.contains("gyc-quick-view-modal") || sectionRoot.closest(".gyc-quick-view-modal");
  
  if (!isQuickView) {
    thumb.scrollIntoView({ block: "nearest", inline: "nearest" });
  } else {
    const scrollContainer = thumb.parentElement;
    if (scrollContainer) {
      const containerStyle = window.getComputedStyle(scrollContainer);
      const isVertical = containerStyle.flexDirection === "column" || containerStyle.display === "grid";
      
      if (isVertical) {
        const thumbTop = thumb.offsetTop;
        const containerHeight = scrollContainer.clientHeight;
        const thumbHeight = thumb.clientHeight;
        scrollContainer.scrollTop = thumbTop - (containerHeight / 2) + (thumbHeight / 2);
      } else {
        const thumbLeft = thumb.offsetLeft;
        const containerWidth = scrollContainer.clientWidth;
        const thumbWidth = thumb.clientWidth;
        scrollContainer.scrollLeft = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);
      }
    }
  }
};

const setGalleryImageByIndex = (sectionRoot, index) => {
  const thumbs = getGalleryThumbs(sectionRoot);
  if (!thumbs.length) return false;

  const safeIndex = ((index % thumbs.length) + thumbs.length) % thumbs.length;
  const thumb = thumbs[safeIndex];
  setActiveGalleryThumb(sectionRoot, thumb);
  setMainGalleryImageFromThumb(sectionRoot, thumb);
  scrollThumbIntoView(sectionRoot, thumb);
  return true;
};

const setGalleryImageByMediaId = (sectionRoot, mediaId) => {
  if (!mediaId) return false;

  const thumb = sectionRoot.querySelector(
    `[data-gallery-thumb][data-media-id="${escapeSelectorValue(mediaId)}"]`,
  );
  if (!thumb) return false;
  if (
    thumb.disabled ||
    thumb.classList.contains("is-unavailable") ||
    thumb.getAttribute("aria-disabled") === "true"
  ) {
    return false;
  }

  setActiveGalleryThumb(sectionRoot, thumb);
  setMainGalleryImageFromThumb(sectionRoot, thumb);
  scrollThumbIntoView(sectionRoot, thumb);
  return true;
};

const moveGalleryByStep = (sectionRoot, step) => {
  const thumbs = getGalleryThumbs(sectionRoot);
  if (thumbs.length < 2) return false;

  const activeIndex = thumbs.findIndex((thumb) =>
    thumb.classList.contains("is-active"),
  );
  const nextIndex = (activeIndex === -1 ? 0 : activeIndex) + step;
  const moved = setGalleryImageByIndex(sectionRoot, nextIndex);
  if (!moved) return false;

  const safeIndex =
    ((nextIndex % thumbs.length) + thumbs.length) % thumbs.length;
  syncVariantFromGalleryThumb(sectionRoot, thumbs[safeIndex]);
  return true;
};

const getGalleryImage = (sectionRoot) =>
  sectionRoot.querySelector(
    "[data-product-main-image], .gyc-product-gallery__image",
  );

const resetGalleryImageMotion = (mainImage) => {
  if (!mainImage) return;

  mainImage.style.removeProperty("transition");
  mainImage.style.removeProperty("transform");
  mainImage.style.removeProperty("opacity");
};

const snapGalleryImageBack = (sectionRoot) => {
  const mainImage = getGalleryImage(sectionRoot);
  if (!mainImage) return;

  mainImage.style.transition = "transform 180ms ease, opacity 180ms ease";
  mainImage.style.transform = "translateX(0)";
  mainImage.style.opacity = "1";
  window.setTimeout(() => resetGalleryImageMotion(mainImage), 190);
};

const animateGallerySwipe = (sectionRoot, direction) => {
  const mainImage = getGalleryImage(sectionRoot);
  if (!mainImage) {
    moveGalleryByStep(sectionRoot, direction);
    return;
  }

  const exitX = direction > 0 ? -72 : 72;
  const enterX = direction > 0 ? 72 : -72;

  mainImage.style.transition = "transform 140ms ease, opacity 140ms ease";
  mainImage.style.transform = `translateX(${exitX}px)`;
  mainImage.style.opacity = "0.28";

  window.setTimeout(() => {
    moveGalleryByStep(sectionRoot, direction);
    mainImage.style.transition = "none";
    mainImage.style.transform = `translateX(${enterX}px)`;
    mainImage.style.opacity = "0.28";
    mainImage.getBoundingClientRect();

    window.requestAnimationFrame(() => {
      mainImage.style.transition =
        "transform 210ms cubic-bezier(0.2, 0.7, 0.2, 1), opacity 210ms ease";
      mainImage.style.transform = "translateX(0)";
      mainImage.style.opacity = "1";
      window.setTimeout(() => resetGalleryImageMotion(mainImage), 230);
    });
  }, 140);
};

const setupGalleryThumbDrag = (galleryRoot) => {
  const thumbRail = galleryRoot.querySelector("[data-product-gallery-thumbs]");
  if (!thumbRail || thumbRail.dataset.dragReady === "true") return;

  thumbRail.dataset.dragReady = "true";
  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let startScrollLeft = 0;
  let startScrollTop = 0;
  let didDrag = false;
  let pressedThumb = null;
  let suppressClickTimer = null;
  const dragThreshold = 10;

  const finishDrag = (event) => {
    if (pointerId === null) return;

    pointerId = null;
    thumbRail.classList.remove("is-dragging");

    if (didDrag) {
      window.clearTimeout(suppressClickTimer);
      thumbRail.dataset.galleryDragMoved = "true";
      suppressClickTimer = window.setTimeout(() => {
        delete thumbRail.dataset.galleryDragMoved;
      }, 180);
    } else {
      const thumb =
        event?.target?.closest?.("[data-gallery-thumb]") || pressedThumb;
      if (thumb && thumbRail.contains(thumb)) {
        if (
          thumb.disabled ||
          thumb.classList.contains("is-unavailable") ||
          thumb.getAttribute("aria-disabled") === "true"
        ) {
          return;
        }
        const sectionRoot =
          thumb.closest(".gyc-quick-view-modal") ||
          thumb.closest(".section-shell") ||
          document;
        setActiveGalleryThumb(sectionRoot, thumb);
        setMainGalleryImageFromThumb(sectionRoot, thumb);
        syncVariantFromGalleryThumb(sectionRoot, thumb);
      }
    }

    pressedThumb = null;
  };

  thumbRail.addEventListener("pointerdown", (event) => {
    if (event.pointerType === "touch") return;
    if (
      !event.isPrimary ||
      (event.pointerType === "mouse" && event.button !== 0)
    )
      return;

    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    startScrollLeft = thumbRail.scrollLeft;
    startScrollTop = thumbRail.scrollTop;
    didDrag = false;
    pressedThumb = event.target?.closest?.("[data-gallery-thumb]");

    try {
      thumbRail.setPointerCapture(pointerId);
    } catch (_) {
      // Some embedded browsers do not allow capture on scroll containers.
    }
  });

  thumbRail.addEventListener("pointermove", (event) => {
    if (pointerId !== event.pointerId) return;

    const deltaX = event.clientX - startX;
    const deltaY = event.clientY - startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const isHorizontalRail = thumbRail.scrollWidth > thumbRail.clientWidth;
    const primaryDelta = isHorizontalRail ? absX : absY;

    if (primaryDelta > dragThreshold) {
      didDrag = true;
      thumbRail.classList.add("is-dragging");
    }

    if (!didDrag) return;

    thumbRail.scrollLeft = startScrollLeft - deltaX;
    thumbRail.scrollTop = startScrollTop - deltaY;
    event.preventDefault();
  });

  thumbRail.addEventListener("pointerup", finishDrag);
  thumbRail.addEventListener("pointercancel", finishDrag);
  thumbRail.addEventListener("lostpointercapture", finishDrag);
};

const setupGalleryStageDrag = (galleryRoot) => {
  const stage = galleryRoot.querySelector(".gyc-product-gallery__stage");
  const sectionRoot = galleryRoot.closest(".gyc-quick-view-modal") || galleryRoot.closest(".section-shell") || document;
  if (
    !stage ||
    stage.dataset.dragReady === "true" ||
    getGalleryThumbs(sectionRoot).length < 2
  )
    return;

  stage.dataset.dragReady = "true";
  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let currentX = 0;
  let currentY = 0;
  let didDrag = false;

  const finishDrag = (event) => {
    if (pointerId === null || (event && pointerId !== event.pointerId)) return;

    const deltaX = currentX - startX;
    const deltaY = currentY - startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    pointerId = null;
    stage.classList.remove("is-dragging");

    if (didDrag) {
      if (absX > 42 && absX > absY * 1.15) {
        animateGallerySwipe(sectionRoot, deltaX < 0 ? 1 : -1);
      } else {
        snapGalleryImageBack(sectionRoot);
      }
      
      stage.dataset.preventClick = "true";
      window.setTimeout(() => {
        delete stage.dataset.preventClick;
      }, 50);
    }
  };

  stage.addEventListener("pointerdown", (event) => {
    if (stage.classList.contains("is-out-of-stock")) return;
    if (
      !event.isPrimary ||
      (event.pointerType === "mouse" && event.button !== 0)
    )
      return;
    if (event.target?.closest?.("[data-gallery-nav]")) return;

    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    currentX = event.clientX;
    currentY = event.clientY;
    didDrag = false;
    resetGalleryImageMotion(getGalleryImage(sectionRoot));

    try {
      stage.setPointerCapture(pointerId);
    } catch (_) {
      // Pointer capture is a convenience here; drag still works without it.
    }
  });

  stage.addEventListener("pointermove", (event) => {
    if (pointerId !== event.pointerId) return;

    currentX = event.clientX;
    currentY = event.clientY;

    const deltaX = event.clientX - startX;
    const deltaY = event.clientY - startY;
    if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
      didDrag = true;
      stage.classList.add("is-dragging");
      const mainImage = getGalleryImage(sectionRoot);
      if (mainImage) {
        const dragX = Math.max(-92, Math.min(92, deltaX * 0.52));
        const fade = Math.max(0.76, 1 - Math.abs(dragX) / 320);
        mainImage.style.transition = "none";
        mainImage.style.transform = `translateX(${dragX}px)`;
        mainImage.style.opacity = String(fade);
      }
      event.preventDefault();
    }
  });

  stage.addEventListener("pointerup", finishDrag);
  stage.addEventListener("pointercancel", finishDrag);
  stage.addEventListener("lostpointercapture", finishDrag);
};

const findBestVariantForSelection = (variants, selectedValues) => {
  const exactAvailable = variants.find(
    (variant) =>
      variant.available && variantMatchesSelection(variant, selectedValues),
  );
  if (exactAvailable) return exactAvailable;

  for (let i = selectedValues.length; i >= 1; i--) {
    const partialSelection = selectedValues.slice(0, i);
    const partialAvailable = variants.find((variant) => {
      if (!variant.available) return false;
      for (let j = 0; j < partialSelection.length; j++) {
        if (partialSelection[j]) {
          const val =
            j === 0
              ? variant.option1
              : j === 1
                ? variant.option2
                : variant.option3;
          if (val !== partialSelection[j]) return false;
        }
      }
      return true;
    });
    if (partialAvailable) return partialAvailable;
  }

  const anyAvailable = variants.find((variant) => variant.available);
  if (anyAvailable) return anyAvailable;

  const exactUnavailable = variants.find((variant) =>
    variantMatchesSelection(variant, selectedValues),
  );
  if (exactUnavailable) return exactUnavailable;

  return variants[0];
};

const getVariantOptionValue = (variant, optionIndex) => {
  if (!variant || !optionIndex) return "";
  if (optionIndex === 1) return variant.option1 || "";
  if (optionIndex === 2) return variant.option2 || "";
  if (optionIndex === 3) return variant.option3 || "";
  return "";
};

const getOptionIndexByName = (optionsRoot, optionName) => {
  const targetName = String(optionName || "")
    .trim()
    .toLowerCase();
  if (!optionsRoot || !targetName) return 0;

  const optionHead = Array.from(
    optionsRoot.querySelectorAll(
      ".gyc-product-info__variant-head[data-option-name][data-option-index]",
    ),
  ).find(
    (head) =>
      String(head.dataset.optionName || "")
        .trim()
        .toLowerCase() === targetName,
  );

  return Number(optionHead?.dataset.optionIndex || 0);
};

const variantColorImageId = (variant) => {
  const id = variant?.color_image?.id;
  return id === undefined || id === null ? "" : String(id);
};

const syncVariantFromGalleryThumb = (sectionRoot, thumb) => {
  if (!thumb?.dataset?.mediaId) return false;

  const form = sectionRoot.querySelector("form.gyc-product-form");
  const optionsRoot = form?.querySelector("[data-product-options]");
  if (!form || !optionsRoot) return false;

  const variants = parseProductVariants(form);
  if (!variants.length) return false;

  let colorOptionIndex = getOptionIndexByName(optionsRoot, "color");
  if (!colorOptionIndex) {
    colorOptionIndex = getOptionIndexByName(optionsRoot, "design") || getOptionIndexByName(optionsRoot, "النمط");
  }
  if (!colorOptionIndex) {
    const heads = optionsRoot.querySelectorAll(".gyc-product-info__variant-head[data-option-index]");
    if (heads.length === 1) {
      colorOptionIndex = 1;
    }
  }
  if (!colorOptionIndex) return false;

  const mediaId = String(thumb.dataset.mediaId);
  const selectedValues = getSelectedOptionValues(optionsRoot);
  const matchedVariants = variants.filter(
    (variant) => variantMediaIds(variant).includes(mediaId),
  );
  if (!matchedVariants.length) return false;

  const nextVariant =
    matchedVariants.find((variant) => {
      const testSelection = [...selectedValues];
      testSelection[colorOptionIndex - 1] = getVariantOptionValue(
        variant,
        colorOptionIndex,
      );
      return variant.available && variantMatchesSelection(variant, testSelection);
    }) ||
    matchedVariants.find((variant) => {
      const testSelection = [...selectedValues];
      testSelection[colorOptionIndex - 1] = getVariantOptionValue(
        variant,
        colorOptionIndex,
      );
      return variantMatchesSelection(variant, testSelection);
    }) ||
    matchedVariants.find((variant) => variant.available) ||
    matchedVariants[0];

  if (!nextVariant) return false;

  syncActiveOptionsFromVariant(optionsRoot, nextVariant);
  updateOptionAvailability(optionsRoot, variants);
  updateGalleryThumbAvailability(sectionRoot, variants);
  updateProductViewFromVariant(form, nextVariant, createMoneyFormatter(form), {
    syncGallery: false,
  });
  return true;
};

const syncLineItemPropertiesFromVariant = (form, variant) => {
  if (!form || !variant) return;

  const optionsRoot = form.querySelector("[data-product-options]");
  const optionHeads = Array.from(
    optionsRoot?.querySelectorAll(
      ".gyc-product-info__variant-head[data-option-name][data-option-index]",
    ) || [],
  );
  const modelInput = form.querySelector('[data-line-item-property="model"]');
  const colorInput = form.querySelector('[data-line-item-property="color"]');
  if (!modelInput && !colorInput) return;

  let modelValue = "";
  let colorValue = "";

  optionHeads.forEach((optionHead) => {
    const optionIndex = Number(optionHead.dataset.optionIndex || 0);
    const optionName = String(optionHead.dataset.optionName || "")
      .trim()
      .toLowerCase();
    const optionValue = getVariantOptionValue(variant, optionIndex);
    if (!optionValue) return;

    if (optionName === "model") modelValue = optionValue;
    if (optionName === "color") colorValue = optionValue;
  });

  if (modelInput) modelInput.value = modelValue;
  if (colorInput) colorInput.value = colorValue;
};

const syncGalleryFromImageReference = (sectionRoot, imageRef) => {
  if (!imageRef) return false;

  const mediaId = imageRef.id || "";
  if (mediaId && setGalleryImageByMediaId(sectionRoot, mediaId)) return true;

  const mainImage = sectionRoot.querySelector(
    "[data-product-main-image], .gyc-product-gallery__image",
  );
  if (!mainImage || !imageRef.src) return false;

  return setMainGalleryImageSource(
    sectionRoot,
    mainImage,
    imageRef.src,
    imageRef.alt || mainImage.alt || "",
    mediaId,
    imageRef.srcset || "",
    imageRef.sizes || mainImage.sizes || "",
  );
};

const syncGalleryFromVariant = (sectionRoot, variant) => {
  const form = sectionRoot.querySelector("form.gyc-product-form");
  const variants = form ? parseProductVariants(form) : [];
  const hasAnyAvailableVariant = variants.some((v) => Boolean(v.available));
  if (!hasAnyAvailableVariant) {
    return false;
  }

  if (syncGalleryFromImageReference(sectionRoot, variant?.color_image))
    return true;

  const featuredRef = variant?.featured_image
    ? {
        id: variant.featured_media?.id || variant.featured_image.id || "",
        src:
          variant.featured_image.src ||
          variant.featured_media?.preview_image?.src ||
          "",
        alt: variant.featured_image.alt || "",
      }
    : null;

  return syncGalleryFromImageReference(sectionRoot, featuredRef);
};

const getStockMessage = (stockEl, variant) => {
  const isAvailable = Boolean(variant?.available);
  if (!isAvailable) {
    return {
      text: stockEl.dataset.outOfStockText || "Out of stock",
      isLowStock: false,
    };
  }

  const quantity = Number(variant?.inventory_quantity);
  const threshold = Number(stockEl.dataset.lowStockThreshold || 5);
  const hasTrackedInventory =
    Boolean(variant?.inventory_management) && Number.isFinite(quantity);

  if (hasTrackedInventory && quantity > 0 && quantity <= threshold) {
    return {
      text: (
        stockEl.dataset.lowStockTemplate ||
        "Hurry! Only __quantity__ left in stock"
      ).replace("__quantity__", String(quantity)),
      isLowStock: true,
    };
  }

  return {
    text:
      stockEl.dataset.sellingFastText ||
      "✨ Selling fast - high demand right now",
    isLowStock: false,
  };
};

const updateProductViewFromVariant = (form, variant, money, options = {}) => {
  const sectionRoot = form.closest(".gyc-quick-view-modal") || form.closest(".section-shell") || document;
  const idInput = form.querySelector('input[name="id"]');
  const addButtons = form.querySelectorAll('button[name="add"]');
  const addTextEls = form.querySelectorAll("[data-add-to-cart-text]");
  const i18n = form.querySelector("[data-money-config]");
  const priceEls = sectionRoot.querySelectorAll("[data-product-price]");
  const comparePriceEls = sectionRoot.querySelectorAll(
    "[data-product-compare-price]",
  );
  const stockEls = sectionRoot.querySelectorAll("[data-product-stock]");
  const selectedLabel = form.querySelector("[data-selected-variant]");
  const selectedColorEls = form.querySelectorAll("[data-selected-color]");

  if (idInput) idInput.value = variant.id;
  priceEls.forEach((priceEl) => {
    priceEl.textContent = money(variant.price);
  });

  comparePriceEls.forEach((comparePriceEl) => {
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      comparePriceEl.textContent = money(variant.compare_at_price);
      comparePriceEl.hidden = false;
    } else {
      comparePriceEl.hidden = true;
    }
  });

  if (options.syncGallery !== false) {
    if (!syncGalleryFromVariant(sectionRoot, variant)) {
      const thumbs = getGalleryThumbs(sectionRoot);
      const fallbackThumb =
        thumbs.find((t) => t.classList.contains("is-active")) || thumbs[0];
      if (fallbackThumb) {
        setActiveGalleryThumb(sectionRoot, fallbackThumb);
        setMainGalleryImageFromThumb(sectionRoot, fallbackThumb);
      }
    }
  }

  if (selectedLabel) selectedLabel.textContent = variant.title || "";
  if (selectedColorEls.length) {
    const activeColor = form.querySelector(
      ".gyc-variant-swatch.is-active[data-option-value]",
    );
    const colorValue =
      variant.color_name ||
      activeColor?.dataset.colorName ||
      activeColor?.dataset.optionValue ||
      "";
    selectedColorEls.forEach((el) => {
      el.textContent = colorValue;
    });
  }

  const isAvailable = Boolean(variant.available);
  addButtons.forEach((addButton) => {
    addButton.disabled = !isAvailable;
    addButton.setAttribute("aria-disabled", String(!isAvailable));
  });
  addTextEls.forEach((addText) => {
    addText.textContent = isAvailable
      ? i18n?.dataset.addToCartText || "Add to cart"
      : i18n?.dataset.soldOutText || "Sold out";
  });
  stockEls.forEach((stockEl) => {
    const stockMessage = getStockMessage(stockEl, variant);
    stockEl.textContent = stockMessage.text;
    stockEl.classList.toggle("is-low-stock", stockMessage.isLowStock);
    stockEl.classList.toggle("is-out-of-stock", !variant.available);
  });

  const quantityInput = form.querySelector('input[name="quantity"]');
  if (quantityInput) {
    const isTracked =
      Boolean(variant.inventory_management) &&
      variant.inventory_policy !== "continue" &&
      Number.isFinite(Number(variant.inventory_quantity)) &&
      Number(variant.inventory_quantity) > 0;

    if (isTracked) {
      const maxQty = Number(variant.inventory_quantity);
      quantityInput.max = String(maxQty);
      const currentQty = Number(quantityInput.value || 1);
      if (currentQty > maxQty) {
        quantityInput.value = String(maxQty);
        quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    } else {
      quantityInput.removeAttribute("max");
    }

    const quantityWrapper = quantityInput.closest("[data-quantity-selector]");
    if (quantityWrapper && typeof window.updateQuantityButtonsState === "function") {
      window.updateQuantityButtonsState(quantityWrapper);
    }
  }

  syncLineItemPropertiesFromVariant(form, variant);
};

const applyVariantSelection = (form, optionIndex, optionValue) => {
  const optionsRoot = form.querySelector("[data-product-options]");
  if (!optionsRoot) return;

  const variants = parseProductVariants(form);
  if (!variants.length) return;

  optionsRoot
    .querySelectorAll(`[data-option-index="${optionIndex}"]`)
    .forEach((button) => button.classList.remove("is-active"));

  let selectedButton = optionsRoot.querySelector(
    `[data-option-index="${optionIndex}"][data-option-value="${escapeSelectorValue(optionValue)}"]`,
  );
  if (!selectedButton && optionValue) {
    const valLower = String(optionValue).trim().toLowerCase();
    selectedButton = Array.from(
      optionsRoot.querySelectorAll(`[data-option-index="${optionIndex}"]`),
    ).find(
      (b) => String(b.dataset.optionValue || "").trim().toLowerCase() === valLower,
    );
  }
  selectedButton?.classList.add("is-active");

  const selectedSelect = optionsRoot.querySelector(
    `[data-option-select][data-option-index="${optionIndex}"]`,
  );
  if (selectedSelect) selectedSelect.value = optionValue;

  const selectedValues = getSelectedOptionValues(optionsRoot);
  const matchedVariant = findBestVariantForSelection(variants, selectedValues);
  if (!matchedVariant) return;

  syncActiveOptionsFromVariant(optionsRoot, matchedVariant);
  updateOptionAvailability(optionsRoot, variants);
  updateGalleryThumbAvailability(
    form.closest(".gyc-quick-view-modal") || form.closest(".section-shell") || document,
    variants,
  );
  updateProductViewFromVariant(
    form,
    matchedVariant,
    createMoneyFormatter(form),
  );
};


const setupZoomBehavior = (modal) => {
  const container = modal.querySelector(".gyc-zoom-modal__container");
  const img = modal.querySelector(".gyc-zoom-modal__image");
  if (!container || !img) return;

  let cachedRect = null;

  const handleZoomStart = (clientX, clientY) => {
    container.classList.add("is-zooming");
    cachedRect = img.getBoundingClientRect();
    handleZoomMove(clientX, clientY);
  };

  const handleZoomMove = (clientX, clientY) => {
    if (!cachedRect) return;
    const x = Math.max(0, Math.min(100, ((clientX - cachedRect.left) / cachedRect.width) * 100));
    const y = Math.max(0, Math.min(100, ((clientY - cachedRect.top) / cachedRect.height) * 100));
    img.style.transformOrigin = `${x}% ${y}%`;
    img.style.transform = "scale(2.5)";
  };

  const handleZoomEnd = () => {
    container.classList.remove("is-zooming");
    img.style.transformOrigin = "center center";
    img.style.transform = "scale(1)";
    cachedRect = null;
  };

  img.addEventListener("mouseenter", (e) => {
    handleZoomStart(e.clientX, e.clientY);
  });

  img.addEventListener("mousemove", (e) => {
    handleZoomMove(e.clientX, e.clientY);
  });

  img.addEventListener("mouseleave", () => {
    handleZoomEnd();
  });

  img.addEventListener("touchstart", (e) => {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      handleZoomStart(touch.clientX, touch.clientY);
    }
  }, { passive: true });

  img.addEventListener("touchmove", (e) => {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      handleZoomMove(touch.clientX, touch.clientY);
      if (e.cancelable) {
        e.preventDefault();
      }
    }
  }, { passive: false });

  img.addEventListener("touchend", () => {
    handleZoomEnd();
  });

  img.addEventListener("touchcancel", () => {
    handleZoomEnd();
  });
};

const openZoomModal = (mainImage) => {
  if (mainImage.closest(".is-out-of-stock")) return;
  const sectionRoot = mainImage.closest(".gyc-quick-view-modal") || mainImage.closest(".section-shell") || document;
  const activeThumb = sectionRoot.querySelector("[data-gallery-thumb].is-active");
  let highResSrc = activeThumb?.dataset?.imageSrc || mainImage.src || "";
  const imageAlt = mainImage.alt || "";

  if (highResSrc) {
    if (highResSrc.includes("width=")) {
      highResSrc = highResSrc.replace(/width=\d+/, "width=2400");
    } else if (highResSrc.includes("cdn.shopify.com")) {
      highResSrc += (highResSrc.includes("?") ? "&" : "?") + "width=2400";
    }
  }

  if (!highResSrc) return;

  let modal = document.getElementById("GycProductZoomModal");
  if (!modal) {
    const html = `
      <div class="gyc-zoom-modal" id="GycProductZoomModal" aria-hidden="true" role="dialog">
        <button class="gyc-zoom-modal__close" aria-label="Close zoom modal">&times;</button>
        <div class="gyc-zoom-modal__loader"></div>
        <div class="gyc-zoom-modal__container">
          <img class="gyc-zoom-modal__image" src="" alt="Product zoom">
        </div>
      </div>
    `;
    document.body.insertAdjacentHTML("beforeend", html);
    modal = document.getElementById("GycProductZoomModal");

    const closeBtn = modal.querySelector(".gyc-zoom-modal__close");
    const closeModal = () => {
      modal.classList.remove("is-open");
      modal.setAttribute("aria-hidden", "true");
      document.body.classList.remove("zoom-modal-open");
      window.setTimeout(() => {
        const img = modal.querySelector(".gyc-zoom-modal__image");
        if (img) img.src = "";
      }, 250);
    };

    closeBtn.addEventListener("click", closeModal);
    modal.addEventListener("click", (e) => {
      if (e.target === modal || e.target.classList.contains("gyc-zoom-modal__container")) {
        closeModal();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && modal.classList.contains("is-open")) {
        closeModal();
      }
    });

    setupZoomBehavior(modal);
  }

  const modalImg = modal.querySelector(".gyc-zoom-modal__image");
  if (modalImg) {
    modal.classList.add("is-loading");
    modalImg.src = highResSrc;
    modalImg.alt = imageAlt;

    const onImageLoad = () => {
      modal.classList.remove("is-loading");
      modalImg.removeEventListener("load", onImageLoad);
      modalImg.removeEventListener("error", onImageError);
    };
    const onImageError = () => {
      modal.classList.remove("is-loading");
      modalImg.removeEventListener("load", onImageLoad);
      modalImg.removeEventListener("error", onImageError);
    };

    modalImg.addEventListener("load", onImageLoad);
    modalImg.addEventListener("error", onImageError);
  }

  modal.classList.add("is-open");
  modal.setAttribute("aria-hidden", "false");
  document.body.classList.add("zoom-modal-open");
};

// Desktop Hover Magnifier Zoom on Product Gallery Main Stage
document.addEventListener("mousemove", (event) => {
  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
  const mainContainer = event.target?.closest?.(".gyc-product-gallery__main");
  if (!mainContainer || mainContainer.classList.contains("is-out-of-stock")) return;

  const img = mainContainer.querySelector("img");
  if (!img) return;

  const rect = mainContainer.getBoundingClientRect();
  const x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
  const y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));

  img.style.transformOrigin = `${x}% ${y}%`;
  img.style.transform = "scale(1.8)";
}, { passive: true });

document.addEventListener("mouseout", (event) => {
  const mainContainer = event.target?.closest?.(".gyc-product-gallery__main");
  if (mainContainer && (!event.relatedTarget || !mainContainer.contains(event.relatedTarget))) {
    const img = mainContainer.querySelector("img");
    if (img) {
      img.style.transformOrigin = "center center";
      img.style.transform = "";
    }
  }
}, { passive: true });

document.addEventListener("click", (event) => {
  if (!event.target || typeof event.target.closest !== "function") return;
  
  const galleryMainTarget = event.target.closest(
    ".gyc-product-gallery__main, .gyc-product-gallery__image, [data-product-main-image], .gyc-product-gallery__zoom-badge"
  );
  if (galleryMainTarget) {
    const stage = galleryMainTarget.closest(".gyc-product-gallery__stage");
    if (stage && stage.dataset.preventClick === "true") {
      return;
    }
    const sectionRoot = galleryMainTarget.closest(".gyc-quick-view-modal") || galleryMainTarget.closest(".section-shell") || document;
    const mainImage = sectionRoot.querySelector("[data-product-main-image], .gyc-product-gallery__image") || galleryMainTarget;
    if (mainImage) {
      openZoomModal(mainImage);
    }
    return;
  }

  const galleryThumb = event.target.closest("[data-gallery-thumb]");
  if (galleryThumb) {
    if (
      galleryThumb.disabled ||
      galleryThumb.classList.contains("is-unavailable") ||
      galleryThumb.getAttribute("aria-disabled") === "true"
    ) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (
      galleryThumb.closest("[data-product-gallery-thumbs]")?.dataset
        .galleryDragMoved === "true"
    ) {
      event.preventDefault();
      return;
    }

    const sectionRoot =
      galleryThumb.closest(".gyc-quick-view-modal") ||
      galleryThumb.closest(".section-shell") ||
      document;
    setActiveGalleryThumb(sectionRoot, galleryThumb);
    setMainGalleryImageFromThumb(sectionRoot, galleryThumb);
    syncVariantFromGalleryThumb(sectionRoot, galleryThumb);
    return;
  }

  const galleryNav = event.target.closest("[data-gallery-nav]");
  if (galleryNav) {
    const sectionRoot = galleryNav.closest(".gyc-quick-view-modal") || galleryNav.closest(".section-shell") || document;
    const direction = galleryNav.dataset.galleryNav === "prev" ? -1 : 1;
    moveGalleryByStep(sectionRoot, direction);
    return;
  }

  const optionButton = event.target.closest(
    "[data-option-index][data-option-value]",
  );
  if (optionButton) {
    const form = optionButton.closest("form");
    if (!form) return;

    const optionIndex = Number(optionButton.dataset.optionIndex || 0);
    const optionValue = optionButton.dataset.optionValue || "";
    if (!optionIndex || !optionValue) return;

    applyVariantSelection(form, optionIndex, optionValue);
    if (optionButton.classList.contains("gyc-variant-swatch")) {
      window.requestAnimationFrame(() =>
        scrollProductGalleryIntoView(
          form.closest(".gyc-quick-view-modal") || form.closest(".section-shell") || document,
        ),
      );
    }
    return;
  }
});



document.addEventListener(
  "mouseover",
  (event) => {
    if (!event.target || typeof event.target.closest !== "function") return;
    const thumb = event.target.closest("[data-gallery-thumb]");
    if (thumb) {
      const thumbImg = thumb.querySelector("img");
      const src =
        thumb.dataset.imageSrc || thumbImg?.currentSrc || thumbImg?.src || "";
      const srcset = thumb.dataset.imageSrcset || "";
      const sizes = thumb.dataset.imageSizes || "";
      if (src) {
        preloadGalleryImage(src, srcset, sizes);
      }
    }
  },
  { passive: true },
);

window.initializeProductSection = (container) => {
  container.querySelectorAll("[data-product-gallery]").forEach((galleryRoot) => {
    const sectionRoot = galleryRoot.closest(".gyc-quick-view-modal") || galleryRoot.closest(".section-shell") || container;
    const form = sectionRoot.querySelector("form.gyc-product-form");
    const variants = form ? parseProductVariants(form) : [];
    const hasAnyAvailableVariant = variants.length === 0 || variants.some((v) => Boolean(v.available));

    preloadAllGalleryImages(sectionRoot);
    setupGalleryThumbDrag(galleryRoot);
    setupGalleryStageDrag(galleryRoot);

    if (!hasAnyAvailableVariant) {
      const allThumbs = Array.from(
        sectionRoot.querySelectorAll("[data-gallery-thumb]"),
      );
      if (allThumbs.length > 0) {
        setActiveGalleryThumb(sectionRoot, allThumbs[0]);
        setMainGalleryImageFromThumb(sectionRoot, allThumbs[0]);
      }
      return;
    }

    const mainImage = sectionRoot.querySelector(
      "[data-product-main-image], .gyc-product-gallery__image",
    );
    const mediaId = mainImage?.dataset?.productMainImageId || "";

    if (mediaId && setGalleryImageByMediaId(sectionRoot, mediaId)) return;

    const thumbs = getGalleryThumbs(sectionRoot);
    if (!thumbs.length) return;

    const activeThumb =
      thumbs.find((thumb) => thumb.classList.contains("is-active")) ||
      thumbs[0];
    setActiveGalleryThumb(sectionRoot, activeThumb);
    setMainGalleryImageFromThumb(sectionRoot, activeThumb);
  });

  container.querySelectorAll("form.gyc-product-form").forEach((form) => {
    const optionsRoot = form.querySelector("[data-product-options]");
    if (!optionsRoot) return;

    const variants = parseProductVariants(form);
    if (!variants.length) return;

    updateOptionAvailability(optionsRoot, variants);

    const currentVariantId = Number(
      form.querySelector('input[name="id"]')?.value || 0,
    );
    const currentVariant =
      variants.find(
        (variant) =>
          Number(variant.id) === currentVariantId && variant.available,
      ) ||
      variants.find((variant) => variant.available) ||
      variants.find((variant) => Number(variant.id) === currentVariantId) ||
      variants[0];
    if (currentVariant) {
      const hasAnyAvailableVariant = variants.some((v) => Boolean(v.available));
      const sectionShell =
        form.closest(".gyc-quick-view-modal") ||
        form.closest(".section-shell") ||
        container;
      syncActiveOptionsFromVariant(optionsRoot, currentVariant);
      updateGalleryThumbAvailability(
        sectionShell,
        variants,
      );

      const isSticker =
        form.dataset.isSticker === "true" ||
        sectionShell.dataset?.isSticker === "true" ||
        sectionShell.querySelector('[data-is-sticker="true"]') !== null;
      const hasExplicitVariantParam = new URLSearchParams(window.location.search).has("variant");
      const shouldSyncGallery = hasAnyAvailableVariant && (!isSticker || hasExplicitVariantParam);

      updateProductViewFromVariant(
        form,
        currentVariant,
        createMoneyFormatter(form),
        { syncGallery: shouldSyncGallery },
      );
    }

    form.querySelectorAll("[data-option-select]").forEach((selectEl) => {
      selectEl.addEventListener("change", (event) => {
        const optionIndex = Number(
          event.currentTarget?.dataset?.optionIndex || 0,
        );
        const optionValue = event.currentTarget?.value || "";
        if (!optionIndex || !optionValue) return;
        applyVariantSelection(form, optionIndex, optionValue);
      });
    });
  });
};

document.addEventListener("DOMContentLoaded", () => {
  window.initializeProductSection(document);
});
